import type { CityProgressDTO } from "@light-earth/contracts";
import { geoNaturalEarth1, geoPath } from "d3-geo";
import type { FeatureCollection, Geometry } from "geojson";
import { useMemo, useState } from "react";
import { feature } from "topojson-client";
import countries from "world-atlas/countries-110m.json";

type Props = {
  cities: CityProgressDTO[];
  selectedCityId: string | undefined;
  onSelectCity: (city: CityProgressDTO) => void;
};

const width = 1_180;
const height = 560;

export function WorldMap({ cities, selectedCityId, onSelectCity }: Props) {
  const [hovered, setHovered] = useState<CityProgressDTO>();
  const projection = useMemo(
    () => geoNaturalEarth1().fitExtent([[22, 26], [width - 22, height - 24]], {
      type: "Sphere"
    }),
    []
  );
  const path = useMemo(() => geoPath(projection), [projection]);
  const land = useMemo(
    () => feature(
      countries,
      countries.objects["countries"]!
    ) as unknown as FeatureCollection<Geometry>,
    []
  );

  return (
    <div className="world-map-shell" aria-label="Live Earth Relay world map">
      <svg
        className="world-map"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-labelledby="map-title map-description"
      >
        <title id="map-title">Cities and verified walks</title>
        <desc id="map-description">
          Candidate cities appear as dim markers. Verified community activity
          appears as a gold glow.
        </desc>
        <defs>
          <radialGradient id="oceanGlow" cx="50%" cy="45%" r="65%">
            <stop offset="0%" stopColor="#174b40" stopOpacity=".64" />
            <stop offset="68%" stopColor="#09251f" stopOpacity=".38" />
            <stop offset="100%" stopColor="#051613" stopOpacity=".1" />
          </radialGradient>
          <linearGradient id="landFill" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#32675a" />
            <stop offset="100%" stopColor="#173b33" />
          </linearGradient>
          <filter id="markerGlow" x="-200%" y="-200%" width="400%" height="400%">
            <feGaussianBlur stdDeviation="7" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <path d={path({ type: "Sphere" }) ?? ""} fill="url(#oceanGlow)" />
        <path
          d={path(land) ?? ""}
          fill="url(#landFill)"
          stroke="#57907f"
          strokeOpacity=".34"
          strokeWidth="0.7"
        />
        {cities.map((city) => {
          const point = projection([city.longitude, city.latitude]);
          if (!point) return null;
          const [x, y] = point;
          const selected = city.id === selectedCityId;
          const lit = city.contributors > 0 || city.activeToday > 0;
          const radius = lit ? 4.5 + city.level * 1.35 : 3.5;
          return (
            <g
              key={city.id}
              className={`city-marker ${lit ? "is-lit" : "is-unlit"} ${selected ? "is-selected" : ""}`}
              transform={`translate(${x} ${y})`}
              role="button"
              tabIndex={0}
              aria-label={`${city.name}, ${city.activeToday} active today`}
              onMouseEnter={() => setHovered(city)}
              onMouseLeave={() => setHovered(undefined)}
              onFocus={() => setHovered(city)}
              onBlur={() => setHovered(undefined)}
              onClick={() => onSelectCity(city)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectCity(city);
                }
              }}
            >
              {lit ? (
                <>
                  <circle className="city-marker__halo" r={radius * 3.2} />
                  <circle className="city-marker__pulse" r={radius * 1.8} />
                </>
              ) : null}
              <circle
                className="city-marker__core"
                r={radius}
                filter={lit ? "url(#markerGlow)" : undefined}
              />
            </g>
          );
        })}
      </svg>
      {hovered ? (
        <div className="map-tooltip" role="status">
          <strong>{hovered.name}</strong>
          <span>{hovered.activeToday.toLocaleString()} walking today</span>
        </div>
      ) : null}
      <div className="map-key">
        <span className="live-dot" aria-hidden="true" />
        GOLD = VERIFIED WALKS · LOCATION COARSENED
      </div>
    </div>
  );
}
